import type { DynamicIncentiveArm } from "../types";
import type { InterviewItemId } from "@/components/interview/types";
import type { ProlificParticipantId } from "@/components/outreach/panel/prolific/types";
import { notNull } from "@/utils/helpers";
import type {
  AudioLanguage,
  FreeResponse,
  ScreenerStatus,
  SurveyId,
} from "@/utils/types";
import seedrandom from "seedrandom";
import { z } from "zod";

export const DYNAMIC_INCENTIVE_FLAG = "dynamic-incentive-experiment";

export const dynamicIncentivePayloadSchema = z.object({
  enabled: z.boolean(),
  studies: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  excludedStudies: z.array(z.string()).optional(),
  excludedOrgs: z.array(z.string()).optional(),
  treatmentPercent: z.number().min(0).max(100).default(50),
  amountUsd: z.number().positive().default(50),
});

export type DynamicIncentiveNotice = { amountUsd: number };

export const getDynamicIncentiveNotice = ({
  data,
  screenerStatus,
  contentLanguage,
  complete,
  screeningInterviewItemIds,
  isPreview,
}: {
  data: Pick<FreeResponse, "history" | "interviewSampleState">;
  screenerStatus: ScreenerStatus;
  contentLanguage: AudioLanguage;
  complete: boolean;
  isPreview: boolean;
  screeningInterviewItemIds: InterviewItemId[];
}): DynamicIncentiveNotice | undefined => {
  const isEnglish = contentLanguage.split("-")[0] === "en";
  const incentive = data.interviewSampleState.dynamicIncentive;
  const screeningItemIds = new Set(screeningInterviewItemIds);
  const hasSubmittedInterviewTurn = data.history.some((state) => {
    const isSubmitted = notNull(state.submittedAt);
    const isScreeningItem = screeningItemIds.has(state.currentItemId);
    return isSubmitted && !isScreeningItem;
  });
  if (
    isPreview ||
    !isEnglish ||
    incentive?.arm !== "treatment" ||
    screenerStatus !== "qualified" ||
    complete ||
    hasSubmittedInterviewTurn
  ) {
    return undefined;
  }
  return { amountUsd: incentive.amountUsd };
};

// A participant keeps the same arm within a study, even when starting again.
export const computeDynamicIncentiveArm = ({
  participantId,
  surveyId,
  treatmentPercent,
}: {
  participantId: ProlificParticipantId;
  surveyId: SurveyId;
  treatmentPercent: number;
}): DynamicIncentiveArm => {
  const draw = seedrandom(`${participantId}:${surveyId}:dynamic-incentive`)();
  return draw * 100 < treatmentPercent ? "treatment" : "control";
};
